/**
 * ATT&CK coverage.
 *
 * The headline number is **weighted** by requirement priority. Unweighted coverage lets a SOC
 * report 90% while every uncovered requirement is Critical — precisely the figure a board should
 * not be given, and precisely the figure an unweighted matrix produces.
 *
 * The matrix beneath it is ordered by kill chain, so a reader can see whether coverage clusters
 * early (detecting reconnaissance and missing the damage) or spans the chain.
 */

import { api, type Coverage as CoverageData } from '../api'
import { useSession } from '../App'
import { Async, Bar, Card, Chip, Note, useAsync } from '../components/ui'

const TACTIC_ORDER = [
  'Reconnaissance',
  'Resource Development',
  'Initial Access',
  'Execution',
  'Persistence',
  'Privilege Escalation',
  'Defense Evasion',
  'Credential Access',
  'Discovery',
  'Lateral Movement',
  'Collection',
  'Command and Control',
  'Exfiltration',
  'Impact',
]

export default function Coverage() {
  const { me } = useSession()
  const coverage = useAsync<CoverageData>(() => api.get<CoverageData>('/coverage'), [me.tenant])

  return (
    <>
      <div className="page-head">
        <div>
          <h1>ATT&amp;CK coverage</h1>
          <div className="sub">
            Measured against the requirements this SOC stated, not against the whole framework.
            Coverage of techniques nobody asked about is not an achievement.
          </div>
        </div>
      </div>

      <Async state={coverage}>
        {(c) => {
          const uncoveredTactics = TACTIC_ORDER.filter((t) => (c.by_tactic[t]?.rule_count ?? 0) === 0)
          const lateStageGaps = uncoveredTactics.filter((t) => c.by_tactic[t]?.late_stage)

          return (
            <div className="stack">
              <div className="grid cols-3">
                <Card title="Weighted coverage" subtitle="By requirement priority">
                  <div style={{ fontSize: 40, fontWeight: 650, letterSpacing: '-0.03em' }} className="tnum">
                    {c.weighted_coverage_pct}%
                  </div>
                  <Bar
                    pct={c.weighted_coverage_pct}
                    tone={c.weighted_coverage_pct >= 80 ? 'ok' : c.weighted_coverage_pct >= 50 ? 'warn' : 'danger'}
                  />
                  <p className="small muted" style={{ marginTop: 8 }}>
                    A Critical requirement counts eight times a Low one, so this number cannot hide
                    an uncovered Critical behind several covered trivia.
                  </p>
                </Card>

                <Card title="Techniques detected">
                  <div style={{ fontSize: 40, fontWeight: 650, letterSpacing: '-0.03em' }} className="tnum">
                    {c.covered_techniques.length}
                  </div>
                  <div className="row wrap" style={{ marginTop: 8, gap: 4 }}>
                    {c.covered_techniques.slice(0, 14).map((t) => (
                      <Chip key={t} tone="outline">
                        {t}
                      </Chip>
                    ))}
                    {c.covered_techniques.length > 14 && (
                      <span className="faint small">+{c.covered_techniques.length - 14}</span>
                    )}
                  </div>
                </Card>

                <Card title="Open gaps">
                  <div
                    style={{ fontSize: 40, fontWeight: 650, letterSpacing: '-0.03em', color: c.gaps.length ? 'var(--danger)' : undefined }}
                    className="tnum"
                  >
                    {c.gaps.length}
                  </div>
                  <p className="small muted" style={{ marginTop: 8 }}>
                    Requirements with a technique no enabled rule detects, or with no configured
                    feed to supply the intelligence.
                  </p>
                </Card>
              </div>

              {lateStageGaps.length > 0 && (
                <Note tone="danger">
                  <strong>No detection in {lateStageGaps.join(', ')}.</strong> These are the stages
                  where dwell time turns into damage. An estate that detects reconnaissance well and
                  nothing at the impact end will see an intrusion begin and not see it finish.
                </Note>
              )}

              {c.missing_feeds.length > 0 && (
                <Note tone="warn">
                  Requirements name feeds that are not configured: <strong>{c.missing_feeds.join(', ')}</strong>.
                  Intelligence the SOC said it needed is not being collected.
                </Note>
              )}

              {c.due_for_review.length > 0 && (
                <Note>
                  {c.due_for_review.length} requirement(s) are due for review ({c.due_for_review.join(', ')}).
                  Requirements that outlive their threat are how a detection estate silts up.
                </Note>
              )}

              {c.gaps.length > 0 && (
                <Card title="Gaps, worst first" subtitle="Ranked by priority, how much is uncovered, and whether anything feeds it" tight>
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Priority</th>
                          <th>Requirement</th>
                          <th>Covered</th>
                          <th>Missing</th>
                          <th>Feed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.gaps.map((g) => (
                          <tr key={g.pir_id}>
                            <td>
                              <Chip tone={g.priority === 'critical' ? 'critical' : g.priority === 'high' ? 'high' : 'medium'}>
                                {g.priority}
                              </Chip>
                            </td>
                            <td>
                              <div style={{ fontWeight: 550 }}>{g.pir_title}</div>
                              <div className="faint small mono">{g.pir_id}</div>
                            </td>
                            <td style={{ minWidth: 120 }}>
                              <Bar pct={g.coverage_pct} tone={g.coverage_pct >= 50 ? 'warn' : 'danger'} />
                              <span className="small tnum">{g.coverage_pct}%</span>
                            </td>
                            <td className="small">
                              {g.uncovered.map((t) => (
                                <Chip key={t.id} tone="outline" title={t.name}>
                                  {t.id}
                                </Chip>
                              ))}
                            </td>
                            <td>
                              {g.has_feed ? (
                                <Chip tone="ok">configured</Chip>
                              ) : (
                                <Chip tone="danger" title="Nothing is collecting the intelligence this requirement needs">
                                  none
                                </Chip>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              <Card
                title="Detection across the kill chain"
                subtitle="Left to right is the order an intrusion advances. Late stages are marked — that is where the response window closes."
              >
                <div className="matrix">
                  {TACTIC_ORDER.map((name) => {
                    const t = c.by_tactic[name]
                    const count = t?.rule_count ?? 0
                    return (
                      <div className="matrix-col" key={name}>
                        <h4>
                          {name}
                          {t?.late_stage && (
                            <>
                              {' '}
                              <Chip tone="warn">late</Chip>
                            </>
                          )}
                          <div className="faint" style={{ fontWeight: 400 }}>
                            {count} rule{count === 1 ? '' : 's'}
                          </div>
                        </h4>
                        {count === 0 ? (
                          <div className="matrix-cell gap">no detection</div>
                        ) : (
                          (t?.techniques ?? []).slice(0, 12).map((tech, i) => (
                            <div className="matrix-cell covered" key={`${tech.id}-${i}`} title={tech.name}>
                              <div className="mono">{tech.id}</div>
                              <div className="faint truncate">{tech.name}</div>
                            </div>
                          ))
                        )}
                      </div>
                    )
                  })}
                </div>
              </Card>
            </div>
          )
        }}
      </Async>
    </>
  )
}
